import { Address } from "viem";

import { InfoGroup } from "#/components/InfoGroup/InfoGroup";
import { InternalAccount } from "#/components/InternalAccount/InternalAccount";
import { useAccounts } from "#/hooks/useAccounts";
import { useKyanSmartAccounts } from "#/services/dynamic/hooks/useKyanSmartAccounts";
import { useAccountState } from "#/store/wallet/account/accountState";
import { type PortfolioSymbolType } from "#/types/markets";
import { getImpactColor } from "#/utils/getImpactColor";
import { getMmrImr } from "#/utils/getMmrImr";

interface SummaryData {
  equity: number;
  amount: string;
  currentImr: number;
  currentMmr: number;
  newImr: number;
  newMmr: number;
}

const generateData = ({
  equity,
  amount,
  currentImr,
  currentMmr,
  newImr,
  newMmr,
}: SummaryData) => [
  {
    data: {
      label: "Total equity",
      change: {
        from: equity,
        to: Number(amount),
        template: "$0.00",
      },
    },
    tooltip:
      "The total value of this account including open positions and available USDC.",
  },
  {
    data: {
      label: "IMr",
      change: {
        from: currentImr * 100,
        to: newImr * 100,
        template: "0.00%",
      },
    },
    tooltip: (
      <>
        <strong>IMr (Initial Margin Ratio) </strong>
        <br />A value greater than or equal to 100% means you have allocated all
        your Equity as margin, and hence have no available balance for
        increasing your position.
      </>
    ),
    leftLabelClassName: getImpactColor(currentImr),
    rightLabelClassName: getImpactColor(newImr),
  },
  {
    data: {
      label: "MMr",
      change: {
        from: currentMmr * 100,
        to: newMmr * 100,
        template: "0.00%",
      },
    },
    tooltip: (
      <>
        <strong>MMr (Maintenance Margin Ratio)</strong>
        <br />A value greater than or equal to 100% means your account is under
        liquidation as there is not enough Equity to maintain open positions.
        Deposit collateral or open offsetting positions to reduce MMr.
      </>
    ),
    leftLabelClassName: getImpactColor(currentMmr),
    rightLabelClassName: getImpactColor(newMmr),
  },
];

export const Summary: React.FC<{
  amount: string;
  market: string;
  account: Address;
}> = ({ amount, market, account }) => {
  const { accounts } = useAccounts();
  const { kyanSmartAccounts } = useKyanSmartAccounts();

  const { portfolioState } = useAccountState(market as PortfolioSymbolType);
  const { imr, mmr } = getMmrImr({
    equity: Number(amount),
    im: portfolioState.im,
    mm: portfolioState.mm,
  });

  const data = generateData({
    amount,
    equity: portfolioState.equity,
    currentImr: portfolioState.imr,
    currentMmr: portfolioState.mmr,
    newImr: imr,
    newMmr: mmr,
  });

  return (
    <div className="flex flex-col gap-1">
      {Boolean(kyanSmartAccounts.length && account) && (
        <div className="flex items-center">
          <InternalAccount
            address={account}
            index={accounts.findIndex((safe) => safe === account)}
          />
          {` - ${market.toUpperCase()}`}
        </div>
      )}

      <InfoGroup rows={data} variant="normal" showDivider={false} />
    </div>
  );
};
